import { ChangeDetectionStrategy, Component, computed, inject, resource, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { MainStore } from 'src/app/common/main.store';
import { CfgCellDirective } from 'src/app/common/table/table-cell.directive';
import { TableColumn } from 'src/app/common/table/table-column.model';
import { tableResource } from 'src/app/common/table/table-resource';
import { CfgTableComponent } from 'src/app/common/table/table.component';
import { CommonService } from 'src/app/services/common.service';
import { DepartmentService } from 'src/app/services/department.service';
import { environment } from 'src/environments/environment';
import { CustomerAssignment, Department } from './customer-administration.model';
import { CustomerAdministrationService } from './customer-administration.service';

@Component({
    selector: 'app-customer-assignment',
    templateUrl: './customer-administration.component.html',
    imports: [FormsModule, NgbDropdownModule, CfgTableComponent, CfgCellDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerAdministrationComponent {
  private readonly store = inject(MainStore);
  private readonly customerAdministrationService = inject(CustomerAdministrationService);
  private readonly departmentService = inject(DepartmentService);
  private readonly toastrService = inject(ToastrService);
  private readonly commonService = inject(CommonService);
  private readonly router = inject(Router);

  readonly accessAllowed = this.store.currentUser.get().isAdmin;
  readonly emptyGuid = this.commonService.guidEmpty();

  readonly departmentsResource = resource({
    loader: async (): Promise<Department[]> => {
      return this.departmentService.getDepartments();
    }
  });

  readonly assignmentsResource = tableResource<CustomerAssignment>(
    () => `${environment.adminConfig.apiUri}/customer/assignments`,
  );

  readonly departments = computed(() => this.departmentsResource.value() ?? []);

  // Toolbar-filtre: har kunden en provisioneret tenant-database / aktive synkroniseringer?
  protected readonly databaseFilter = signal<'all' | 'yes' | 'no'>('all');
  protected readonly syncFilter = signal<'all' | 'yes' | 'no'>('all');

  // Afdelingsdata joines på — vises via [rows]; tilstanden (loading/fejl/tom) ejes af assignmentsResource.
  readonly assignments = computed(() => {
    const assignments = this.assignmentsResource.value() ?? [];
    const departments = this.departments();
    const databaseFilter = this.databaseFilter();
    const syncFilter = this.syncFilter();

    return assignments
      .filter(assignment => databaseFilter === 'all' || assignment.hasDatabase === (databaseFilter === 'yes'))
      .filter(assignment => syncFilter === 'all' || assignment.readonly === (syncFilter === 'yes'))
      .map(assignment => {
      const department = departments.find(d => d.id === assignment.departmentId);
      return {
        ...assignment,
        departmentCode: department?.code,
        departmentName: department?.name
      } as CustomerAssignment;
    });
  });

  // Gem-i-gang pr. kunde — immutabel opdatering, så OnPush/cfg-table ser ændringen.
  private readonly savingIds = signal<ReadonlySet<number>>(new Set());

  protected readonly columns: TableColumn<CustomerAssignment>[] = [
    { key: 'customerId', header: 'Id', width: '80px', type: 'number' },
    { key: 'name', header: 'Kundenavn' },
    { key: 'department', header: 'Afdeling', width: '400px', value: item => item.departmentName },
    { key: 'active', header: 'Aktiv', width: '40px', type: 'boolean', value: item => item.active },
    { key: 'hasDatabase', header: 'Har database', width: '110px', type: 'boolean', headerClass: 'text-center', cellClass: 'text-center', interactive: false },
    // Syntetisk kolonne — `readonly` betyder "kunden har aktive synkroniseringer" (og låser redigering).
    { key: 'hasSynchronizations', header: 'Har synk.', width: '90px', type: 'boolean', value: item => item.readonly, headerClass: 'text-center', cellClass: 'text-center', interactive: false },
    { key: 'status', header: ' ', width: '50px', sortable: false },
    { key: 'actions', header: ' ', width: '60px', sortable: false },
  ];

  protected readonly trackById = (item: CustomerAssignment) => item.customerId;

  isSaving(item: CustomerAssignment): boolean {
    return this.savingIds().has(item.customerId);
  }

  // --- Genveje pr. kunde: hop direkte til de relevante sider uden selv at skulle skifte afdeling ---

  openCustomer(item: CustomerAssignment): void {
    // Dashboardet skifter selv den valgte afdeling til kundens efter load.
    this.router.navigate(['/customer-dashboard', item.customerId]);
  }

  openTemplates(item: CustomerAssignment): void {
    this.swapToCustomersDepartment(item);
    this.router.navigate(['/templates']);
  }

  openSynchronizations(item: CustomerAssignment): void {
    this.swapToCustomersDepartment(item);
    // Synk-listen prefiltreres på kunde-id via cfg-tabellens URL-synkede søgning.
    this.router.navigate(['/synchronizations'], { queryParams: { search: item.customerId } });
  }

  // Skifter kun til afdelinger fra brugerens egen liste — findes kundens afdeling ikke dér,
  // har brugeren ikke adgang, og den valgte afdeling forbliver urørt.
  private swapToCustomersDepartment(item: CustomerAssignment): void {
    const department = this.departments().find(d => d.id === item.departmentId);
    if (department) {
      this.departmentService.setSelectedDepartment(department);
    }
  }

  onDepartmentChange(item: CustomerAssignment) {
    this.saveCustomerAssignment(item);
  }

  onActiveChange(item: CustomerAssignment) {
    this.saveCustomerAssignment(item);
  }

  private saveCustomerAssignment(item: CustomerAssignment)
  {
    this.savingIds.update(ids => new Set(ids).add(item.customerId));
    this.customerAdministrationService.saveCustomerAssignment(item)
    .then((newitem: CustomerAssignment) => {
      item.customerKundeId = newitem.customerKundeId;
      this.toastrService.success(`Kunde ${item.customerId} - ${item.name} er opdateret.`);
    })
    .catch(() => {
      // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
    })
    .finally(() => {
      this.savingIds.update(ids => {
        const next = new Set(ids);
        next.delete(item.customerId);
        return next;
      });
    });
  }
}
